import { useCallback, useEffect, useState } from "react";
import {
  listarCentros,
  crearCentro,
  actualizarCentro,
  eliminarCentro,
} from "../../../../services/centrosService";

const initialFormState = {
  nombre: "",
  alias: "",
  ciudad: "",
  ruc: "",
  direccion: "",
  telefono: "",
  email: "",
  representante_legal: "",
  representante_profesion: "",
  representante_cargo: "",
  representante_cedula: "",
  representante_firma_path: "",
  logo_path: "",
  ministerio_logo_path: "",
  fondo_mencion_path: "",
  header_text_image_path: "",
  footer_image_path: "",
  logo_file: null,
  ministerio_logo_file: null,
  fondo_mencion_file: null,
  representante_firma_file: null,
  header_text_image_file: null,
  footer_image_file: null,
  activo: true,
};

const normalizeCentro = (centro = {}) => ({
  ...centro,
  activo:
    typeof centro.activo === "boolean"
      ? centro.activo
      : centro.activo === 1 || centro.activo === "1",
});

const buildPayload = (form) => {
  const fd = new FormData();
  const append = (key, val) => {
    if (val !== undefined && val !== null) fd.append(key, val);
  };

  append("nombre", form.nombre?.trim() || "");
  append("alias", form.alias?.trim() || "");
  append("codigo_mdi", form.codigo_mdi?.trim() || "");
  append("ciudad", form.ciudad?.trim() || "");
  append("ruc", form.ruc?.trim() || "");
  append("direccion", form.direccion?.trim() || "");
  append("telefono", form.telefono?.trim() || "");
  append("email", form.email?.trim() || "");
  append("representante_legal", form.representante_legal?.trim() || "");
  append("representante_profesion", form.representante_profesion?.trim() || "");
  append("representante_cargo", form.representante_cargo?.trim() || "");
  append("representante_cedula", form.representante_cedula?.trim() || "");
  append("representante_firma_path", form.representante_firma_path || "");
  append("logo_path", form.logo_path || "");
  append("ministerio_logo_path", form.ministerio_logo_path || "");
  append("fondo_mencion_path", form.fondo_mencion_path || "");
  append("header_text_image_path", form.header_text_image_path || "");
  append("footer_image_path", form.footer_image_path || "");
  append("activo", form.activo ? 1 : 0);

  if (form.logo_file) fd.append("logo", form.logo_file);
  if (form.ministerio_logo_file) fd.append("ministerio_logo", form.ministerio_logo_file);
  if (form.fondo_mencion_file) fd.append("fondo_mencion", form.fondo_mencion_file);
  if (form.representante_firma_file) fd.append("representante_firma", form.representante_firma_file);
  if (form.header_text_image_file) fd.append("header_text_image", form.header_text_image_file);
  if (form.footer_image_file) fd.append("footer_image", form.footer_image_file);

  return fd;
};

const validarCentro = (form) => {
  if (!form.nombre?.trim()) return "El nombre es obligatorio.";
  return null;
};

export default function useCentrosLogic() {
  const [centros, setCentros] = useState([]);
  const [centroSeleccionado, setCentroSeleccionado] = useState(null);
  const [modo, setModo] = useState("");
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEdicion, setSavingEdicion] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const showAlert = useCallback(
    (type, message) => setAlerta({ type, message }),
    []
  );
  const dismissAlert = useCallback(() => setAlerta(null), []);

  const loadCentros = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarCentros();
      setCentros((Array.isArray(data) ? data : []).map(normalizeCentro));
      setListError(null);
    } catch (error) {
      console.error(error);
      setListError(error.message || "No se pudo obtener la lista de centros.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCentros();
  }, [loadCentros]);

  const resetForms = useCallback(() => {
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
  }, []);

  const abrirModalNuevo = useCallback(() => {
    resetForms();
    setModo("nuevo");
  }, [resetForms]);

  const abrirModalEditar = useCallback((centro) => {
    const normalized = normalizeCentro(centro);
    setCentroSeleccionado(normalized);
    setFormEditar({ ...initialFormState, ...normalized });
    setModo("editar");
  }, []);

  const abrirModalEliminar = useCallback((centro) => {
    setCentroSeleccionado(normalizeCentro(centro));
    setModo("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setCentroSeleccionado(null);
    setModo("");
    resetForms();
  }, [resetForms]);

  const handleCrearCentro = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarCentro(formNuevo);
      if (validation) {
        showAlert("warning", validation);
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo);
        const nuevo = await crearCentro(payload);
        setCentros((prev) => [...prev, normalizeCentro(nuevo)]);
        showAlert("success", "Centro creado correctamente.");
        cerrarModal();
      } catch (error) {
        console.error(error);
        showAlert("danger", error.message || "No se pudo crear el centro.");
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, cerrarModal, showAlert]
  );

  const handleActualizarCentro = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!centroSeleccionado) return;

      const validation = validarCentro(formEditar);
      if (validation) {
        showAlert("warning", validation);
        return;
      }

      setSavingEdicion(true);
      try {
        const payload = buildPayload(formEditar);
        const actualizado = await actualizarCentro(centroSeleccionado.id, payload);
        setCentros((prev) =>
          prev.map((centro) =>
            centro.id === centroSeleccionado.id
              ? normalizeCentro(actualizado)
              : centro
          )
        );
        showAlert("success", "Centro actualizado correctamente.");
        cerrarModal();
      } catch (error) {
        console.error(error);
        showAlert("danger", error.message || "No se pudo actualizar el centro.");
      } finally {
        setSavingEdicion(false);
      }
    },
    [centroSeleccionado, formEditar, cerrarModal, showAlert]
  );

  const handleEliminarCentro = useCallback(async () => {
    if (!centroSeleccionado) return;
    const id = centroSeleccionado.id;
    setDeleting(true);
    try {
      await eliminarCentro(id);
      setCentros((prev) => prev.filter((centro) => centro.id !== id));
      showAlert("success", "Centro eliminado correctamente.");
      cerrarModal();
    } catch (error) {
      console.error(error);
      showAlert("danger", error.message || "No se pudo eliminar el centro.");
    } finally {
      setDeleting(false);
    }
  }, [centroSeleccionado, cerrarModal, showAlert]);

  return {
    centros,loading,listError,alerta,dismissAlert,modo,formNuevo,setFormNuevo,formEditar,setFormEditar,savingNuevo,
    savingEdicion,
    deleting,
    centroSeleccionado,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearCentro,
    handleActualizarCentro,
    handleEliminarCentro,
    loadCentros,
  };
}

export { initialFormState };
